import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { safePath } from '../utils/authCallbackUrls';

const RETURN_URL_KEY = 'bikendure_return_url';

/**
 * `/` is not a page on this subdomain. The bike-fit landing page lives on the
 * marketing site (bikendure.com); bikefit.bikendure.com is the application only.
 * Everyone who lands on `/` is forwarded to the real app entry point.
 *
 * Three details are load-bearing:
 *
 * 1. This must stay a client-side redirect. `bikendure_return_url` is stashed in
 *    sessionStorage (by FitResultsPage) and can only be read by JS, so a
 *    Vercel-level 301 would fire before any JS runs and lose that state. Note
 *    that sign-in itself does NOT depend on this: Better Auth is always handed a
 *    callbackURL with the destination already baked in (see
 *    `utils/authCallbackUrls.ts`), so those users land on their destination
 *    directly and never pass through here. The return-URL branch below is a
 *    safety net for anything that reaches `/` with a pending destination —
 *    bookmarks, stale links, a hand-typed origin.
 *
 * 2. We wait for `isLoaded`. Redirecting while auth is still resolving would
 *    skip the return-URL check entirely and produce the same lost-state bug.
 *
 * 3. The redirect is issued from an effect keyed on `location.key`, not from a
 *    `<Navigate>` element. `<Navigate>` navigates from an effect keyed on the
 *    resolved path, so if anything bounces the user back to `/` in the same
 *    commit (AuthReturnHandler used to do exactly that) the path is unchanged,
 *    the effect never re-runs, and `/` becomes a terminal blank page that only a
 *    full reload escapes. Keying on `location.key` re-arms the redirect for any
 *    such bounce, present or future.
 *
 * Storage is read during render but only cleared in an effect: StrictMode
 * double-invokes render and commits the SECOND call's output, so removing the
 * key mid-render would make the second invocation read `null` and drop the
 * destination.
 */
const HomeRedirect: React.FC = () => {
  const { isSignedIn, isLoaded } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const stored = isLoaded && isSignedIn ? sessionStorage.getItem(RETURN_URL_KEY) : null;

  useEffect(() => {
    if (stored) sessionStorage.removeItem(RETURN_URL_KEY);
  }, [stored]);

  // `safePath` collapses anything off-origin (e.g. '//evil.com') to '/', which is
  // this very route — treat that as "no usable destination" rather than looping.
  const safeReturnUrl = stored ? safePath(stored) : '/';
  const destination = safeReturnUrl === '/' ? '/onboarding' : safeReturnUrl;

  useEffect(() => {
    if (!isLoaded) return;
    navigate(destination, { replace: true });
  }, [isLoaded, destination, navigate, location.key]);

  return null;
};

export default HomeRedirect;
